import type { TaxTerm } from './TaxTermHelp';

export type ChecklistRowKey = 'jurisdictions' | 'stateWithholding' | 'unemployment' | 'paidLeaveDisability' | 'localTaxes' | 'reciprocity' | 'status';

/** The guidance an open row shows. Every sentence is plain language and says only what EPN does today. */
export interface RowGuidance {
  /** The question this row answers. */
  question: string;
  /** Why EPN needs it. */
  why: string;
  /** Where the answer is found. */
  where: string;
  /** Whether the value comes from an agency notice. */
  agencyNotice: string;
  /** What the answer looks like. Synthetic format only, never a real number or rate. */
  example: string;
  /** Help terms shown beside the row's title. */
  terms: TaxTerm[];
}

export interface ChecklistRowDefinition {
  key: ChecklistRowKey;
  title: string;
  /** Requirement `section`s that belong to this row. */
  sections: string[];
  guidance: RowGuidance;
}

/** The SUI explanation, verbatim from the contract (section 5). */
export const SUI_EXPLANATION =
  "EPN uses the employee's work arrangement and your employer registrations to determine the unemployment-insurance jurisdiction. Use an override only when supported by an agency determination.";
export const SUI_OVERRIDE_NOTE = 'Overrides are not available yet.';

/** Said on both screens: the status is informational and does not make payroll ready. */
export const INFORMATIONAL_NOTICE =
  'This status is informational. EPN has not yet certified payroll tax calculation for any jurisdiction, so it does not mean payroll is ready and it changes no withholding.';

export const CHECKLIST_ROWS: readonly ChecklistRowDefinition[] = [
  {
    key: 'jurisdictions',
    title: 'Work and Residence Jurisdictions',
    sections: ['jurisdictions', 'multiState'],
    guidance: {
      question: 'Which state does this employee work in, and which state do they live in?',
      why: 'Payroll taxes depend on where the employee works and where they live. EPN reads both from records it already holds, so nobody types them here.',
      where: 'The work state comes from the primary work location. The residence state comes from the home address, both in Employee Setup.',
      agencyNotice: 'No. These come from the employee\'s own addresses and work location, not from an agency notice.',
      example: 'Work state: New Jersey. Residence state: Pennsylvania.',
      terms: ['residentTax', 'workLocationTax'],
    },
  },
  {
    key: 'stateWithholding',
    title: 'State or Territory Withholding',
    sections: ['stateWithholding'],
    guidance: {
      question: 'What did the employee answer on the state income tax withholding form?',
      why: 'A state\'s withholding form tells an employer how much income tax to hold back. EPN keeps the employee\'s answers as a draft for a reviewer. Payroll does not use them yet.',
      where: 'The employee completes it in their own setup, under State Income Tax. Their saved answers can be read here but not changed.',
      agencyNotice: 'No. These are the employee\'s own answers on the state\'s form, not an agency notice.',
      example: 'Filing status: Single. Total allowances: 1.',
      terms: ['withholdingAccount', 'depositSchedule', 'filingFrequency'],
    },
  },
  {
    key: 'unemployment',
    title: 'Unemployment Insurance',
    sections: ['unemployment'],
    guidance: {
      question: 'Which state\'s unemployment insurance applies, and is your company registered with it?',
      why: 'Employers pay unemployment insurance to the state where the employee works. EPN needs that state, your company\'s account number and your rate so it can calculate, pay and report it later.',
      where: 'The registration letter and the yearly rate notice from the state\'s unemployment agency.',
      agencyNotice: 'Yes. The account number and the rate come from the state agency\'s notices.',
      example: 'An account number written the way the agency prints it, and a rate written as a percent.',
      terms: ['sui', 'futa', 'employerTaxRate', 'taxableWageBase'],
    },
  },
  {
    key: 'paidLeaveDisability',
    title: 'Paid Leave and Disability',
    sections: ['paidLeaveDisability'],
    guidance: {
      question: 'Does this state require paid family leave or disability coverage, and does your company use an approved private plan?',
      why: 'Some states run their own paid leave or disability programs with separate contributions. EPN cannot yet tell which programs apply, so a reviewer has to confirm it.',
      where: 'The state\'s paid leave or disability agency, or a notice it sent your company.',
      agencyNotice: 'Yes. Program accounts and approved private plans are confirmed by an agency notice.',
      example: 'The program name and the account number the agency issued, if it issued one.',
      terms: ['privatePlan'],
    },
  },
  {
    key: 'localTaxes',
    title: 'Local Payroll Taxes',
    sections: ['localTaxes'],
    guidance: {
      question: 'Does a city, county, school district or similar local jurisdiction tax this employee\'s pay?',
      why: 'Some places charge their own payroll taxes based on where the employee works or lives. EPN cannot determine local jurisdictions yet, so it never assumes there are none.',
      where: 'Notices from local tax agencies, your company\'s local registrations, and the employee\'s addresses.',
      agencyNotice: 'Sometimes. A local registration or rate usually comes from the local agency\'s notice.',
      example: 'A city or county name, and whether the tax is based on where the employee works or lives.',
      terms: ['residentTax', 'workLocationTax'],
    },
  },
  {
    key: 'reciprocity',
    title: 'Reciprocity and Exemptions',
    sections: ['reciprocity'],
    guidance: {
      question: 'Does the employee live in a different state from where they work, and do the two states have a reciprocity agreement?',
      why: 'With a reciprocity agreement an employee may be able to avoid income tax in the work state by giving their employer a signed form. EPN records the request and the reviewer\'s decision. It does not change withholding yet.',
      where: 'The form for the employee\'s pair of states comes from the work state\'s tax agency. The employee uploads the signed copy.',
      agencyNotice: 'The form comes from the agency. EPN does not receive agency notices about it.',
      example: 'A certificate of nonresidence for a Pennsylvania resident who works in New Jersey.',
      terms: ['reciprocity'],
    },
  },
  {
    key: 'status',
    title: 'Tax Setup Status',
    sections: [],
    guidance: {
      question: 'Is everything EPN needs for this employee\'s tax setup in place?',
      why: 'This row sums up the rows above so you can see what is left. It is a to-do list for people, not a payroll check.',
      where: 'It is worked out from the information EPN holds. Open a row above to see what to do.',
      agencyNotice: 'No. It is worked out by EPN from the other rows.',
      example: '3 complete, 2 need attention.',
      terms: [],
    },
  },
];
